import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
} from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type { ApplicationEditions } from '@atlassian/jira-shared-types/src/edition.tsx';
import { consumeDependency } from '@atlassian/jira-router-resources-utils/src/utils/consume-dependency/index.tsx';
import { projectIdResource } from '../project-id/index.tsx';
import type { KBTopicsResponse, FetchSuggestedTopicsResponse } from './types.tsx';

type AiEnablementResponse = {
	readonly isEnabled: boolean;
};
type ProjectResponse = {
	readonly id: string;
};

const isTopicSuggestionAllowed = (appEditions: ApplicationEditions) => {
	return appEditions.serviceDesk === 'PREMIUM_EDITION';
};

export const kbTopicsResource = createResource<KBTopicsResponse>({
	type: 'KB_TOPIC',
	depends: [projectIdResource.type],
	getKey: ({ match }) => {
		return match.params.projectKey ? `kb-topic-${match.params.projectKey}` : 'kb-topic';
	},
	getData: async (
		dataContext: RouterDataContext,
		{ tenantContext: { activationId, appEditions } }: ResourceStoreContext,
	) => {
		const {
			match: { params },
		} = dataContext;
		if (!isTopicSuggestionAllowed(appEditions)) {
			return {
				topicSuggestionAllowed: false,
			};
		}

		if (fg('jsm-kb-articles-functional')) {
			// Use shared project ID resource - fetch project ID and AI enablement in parallel
			const [projectData, aiEnablementResponse] = await Promise.all([
				consumeDependency<{ id: string }>(dataContext.dependencies, projectIdResource.type),
				fetchJson<AiEnablementResponse>('/rest/api/1/aiconfig/ai-enabled/jira-servicedesk', {
					method: 'GET',
				}),
			]);

			if (!projectData?.id) {
				return {
					topics: [],
					isFiltered: true,
					aiEnabled: false,
					topicSuggestionAllowed: true,
				};
			}

			// if ai enablement is enabled, fetch kb topics
			if (aiEnablementResponse.isEnabled) {
				const response = await fetchJson<FetchSuggestedTopicsResponse>(
					`/gateway/api/jsd-apollo-stargate/sharded/workspace/${activationId}/api/project/${projectData.id}/kb-topics`,
				);
				return {
					topics: response.topics,
					aiEnabled: true,
					topicSuggestionAllowed: true,
				};
			}
			return {
				topics: [],
				aiEnabled: false,
				topicSuggestionAllowed: true,
			};
		}
		// fetch project response and ai enablement response in parallel
		const [projectResponse, aiEnablementResponse] = await Promise.all([
			fetchJson<ProjectResponse>(`/rest/api/2/project/${params.projectKey}`),
			fetchJson<AiEnablementResponse>('/rest/api/1/aiconfig/ai-enabled/jira-servicedesk', {
				method: 'GET',
			}),
		]);
		// if ai enablement is enabled, fetch kb topics
		if (aiEnablementResponse.isEnabled) {
			const response = await fetchJson<FetchSuggestedTopicsResponse>(
				`/gateway/api/jsd-apollo-stargate/sharded/workspace/${activationId}/api/project/${projectResponse.id}/kb-topics`,
			);
			return {
				topics: response.topics,
				aiEnabled: true,
				topicSuggestionAllowed: true,
			};
		}
		return {
			topics: [],
			aiEnabled: false,
			topicSuggestionAllowed: true,
		};
	},
	isBrowserOnly: true,
	maxAge: 1000 * 30 * 60, // 30 minutes max age
});
